"use client"
import MapBoxMapWrapper from "@/components/shared/MapBoxMapWrapper";
import React, { useState, useEffect, useCallback } from "react";
import LocatorSearchComponent from "@/components/locator/LocatorSearchComponent";
import LocatorSearchResultsComponent from "@/components/locator/LocatorSearchResultsComponent";
import LocatorSearchMenu from "@/components/locator/LocatorSearchMenu";
import useApi from "@/components/api/useApi";
import useUtils from "@/components/shared/useUtils";
import MapBoxMapPopup from "@/components/shared/MapBoxMapPopup";
import * as turf from "@turf/turf";
import { useConfig } from "@/lib/providers/ConfigProvider";
import { useMediaQuery } from "react-responsive";
import { useCurrentLocation } from "@/lib/providers/GeolocationProvider";
import RESearchResultPreview from "@/components/locator/re/RESearchResultPreview";
import RELocatorFilters from "@/components/locator/re/RELocatorFilters";
import REListingModal from "@/components/locator/re/REListingModal";
import { filterREFeatures } from "@/components/locator/re/reLayer";
import PinLayerPointPopup from "@/components/assetmanagement/PinLayerPointPopup";
import LocatorFullscreenControl from "@/components/locator/LocatorFullscreenControl";
import LocatorBrandBadge from "@/components/locator/LocatorBrandBadge";
import useBoundaryDefaultCenter from "@/components/locator/useBoundaryDefaultCenter";
import useLocatorEventMarkers, { LocatorEventPopup } from "@/components/locator/useLocatorEventMarkers";

let moment = require("moment");

const RELocatorContainer = ({
  profile,
  community,
  token,
  backgroundGeoJson,
  systemLayers,
  pinLayers,
  eventEntries,
  allowFullscreen = false,
  brandBadge,
  // { pitch, bearing } for the 2.5D view (see MapBoxMap), else null
  perspective = null
}) => {
  const isSm = useMediaQuery({ query: "(max-width: 767.98px)" });
  const { currentLocation } = useCurrentLocation();
  const utils = useUtils();
  const { apiUrl } = useConfig();
  const [cssLoaded, setCssLoaded] = useState(false);

  useEffect(() => {
    if (profile && !cssLoaded) {
      utils.loadDynamicCss(
        `${apiUrl}/api/profiles/${profile.resourceName}/community/landing_page.css`
      );
      setCssLoaded(true);
    }
  }, [profile, cssLoaded]);

  const searchOptions = [
    {
      label: "Residential",
      value: "RESIDENTIAL",
      icon: "fa-house",
      searchType: "CATEGORY",
      searchTerms: ["residential"]
    },
    {
      label: "Office",
      value: "OFFICE",
      icon: "fa-chair-office",
      searchType: "CATEGORY",
      searchTerms: ["office"]
    },
    {
      label: "Industrial",
      value: "INDUSTRIAL",
      icon: "fa-industry",
      searchType: "CATEGORY",
      searchTerms: ["industrial"]
    },
    {
      label: "Retail",
      value: "RETAIL",
      icon: "fa-bag-shopping",
      searchType: "CATEGORY",
      searchTerms: ["retail"]
    },
    {
      label: "Medical",
      value: "MEDICAL",
      icon: "fa-kit-medical",
      searchType: "CATEGORY",
      searchTerms: ["medical"]
    },
    {
      label: "Food",
      value: "FOOD",
      icon: "fa-fork-knife",
      searchType: "CATEGORY",
      searchTerms: ["food"]
    },
    {
      label: "Event",
      value: "EVENT",
      icon: "fa-party-bell",
      searchType: "CATEGORY",
      searchTerms: ["event"]
    },
    {
      label: "Storage",
      value: "STORAGE",
      icon: "fa-warehouse",
      searchType: "CATEGORY",
      searchTerms: ["storage"]
    },
    {
      label: "Other",
      value: "OTHER",
      icon: "fa-kit fa-solid-building-circle-question",
      searchType: "CATEGORY",
      searchTerms: ["other"]
    }
  ];

  const [defaultCenter, setDefaultCenter] = useState(null);
  const [boundingBox, setBoundingBox] = useState(null);
  const [mapPopup, setMapPopup] = useState(null);

  const [rESearchIndex, setRESearchIndex] = useState(null);
  const [allSearchOptions, setAllSearchOptions] = useState(null);

  const [searchText, setSearchText] = useState("");

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [textQuery, setTextQuery] = useState(null);

  const [searchResults, setSearchResults] = useState(null);
  const [filteredResults, setFilteredResults] = useState(null);
  const [itemHovered, setItemHovered] = useState(null);

  const [itemSelected, setItemSelected] = useState(null);

  const api = useApi();

  const [locatorGeoJson, setLocatorGeoJson] = useState(null);
  const [buildingsGeoJson, setBuildingsGeoJson] = useState(null);
  const [locatorFilter, setLocatorFilter] = useState(null);
  const [filteredLocatorGeoJson, setFilteredLocatorGeoJson] = useState(null);
  const [currentFilter, setCurrentFilter] = useState(null);

  useEffect(() => {
    if (profile && !locatorGeoJson) {
      api
        .cms_getRELocatorGeoJson(profile.resourceName, token)
        .then(geoJson => {
          setLocatorGeoJson(geoJson);
          let searchableREIndex = buildSearchableREIndex(geoJson);
          setRESearchIndex(searchableREIndex);
          setBoundingBox(geoJson?.bbox);
          let turfCenter = turf.center(geoJson);
          setDefaultCenter({
            longitude: turfCenter.geometry.coordinates[0],
            latitude: turfCenter.geometry.coordinates[1]
          });
        })
        .catch(error => {
          console.error("Error getting RE locator geojson", error);
        });
    }
  }, [profile, token, locatorGeoJson]);

  useEffect(() => {
    if (profile && !buildingsGeoJson) {
      api
        .cms_getREBuildingsGeoJson(profile.resourceName, token)
        .then(geoJson => {
          if (geoJson?.features?.length) {
            setBuildingsGeoJson(geoJson);
          }
        })
        .catch(error => {
          console.error("Error getting RE buildings geojson", error);
        });
    }
  }, [profile, token]);

  const buildSearchableREIndex = geoJson => {
    const searchableREIndex = geoJson.features?.map(feature => {
      const properties = feature.properties;
      return {
        id: properties.id,
        value: properties.id,
        label: properties.addressLine1,
        searchTerms: [properties.addressLine1],
        icon: "location-pin",
        searchType: "RE",
        type: properties.type
      };
    });
    return searchableREIndex;
  };

  const onREFilterChange = ({
    saleOrLease,
    category,
    minAreaSqFt,
    maxAreaSqFt,
    minPrice,
    maxPrice,
    selectedAmenities
  }) => {
    setCurrentFilter({
      saleOrLease,
      category,
      minAreaSqFt,
      maxAreaSqFt,
      minPrice,
      maxPrice,
      selectedAmenities
    });
    if (locatorGeoJson) {
      const filtered = filterREFeatures(locatorGeoJson.features, {
        saleOrLease,
        category,
        minAreaSqFt,
        maxAreaSqFt,
        minPrice,
        maxPrice,
        selectedAmenities
      });

      setLocatorFilter(filtered.map(feature => feature.properties.id));
    }
  };

  useEffect(() => {
    if (locatorGeoJson) {
      if (locatorFilter) {
        setFilteredLocatorGeoJson({
          ...locatorGeoJson,
          features: locatorGeoJson.features.filter(feature =>
            locatorFilter.includes(feature.properties.id)
          )
        });
      } else {
        setFilteredLocatorGeoJson(locatorGeoJson);
      }
    }
  }, [locatorGeoJson, locatorFilter]);

  const mapPopupElement = (mapWithEvt, item) => {
    return (
      <MapBoxMapPopup
        anchor="bottom"
        latitude={mapWithEvt.lngLat.lat}
        longitude={mapWithEvt.lngLat.lng}
      >
        <div style={{ position: "relative" }}>
          <RESearchResultPreview
            item={item}
            selectedCategory={selectedCategory}
            showThumbnail={item.selectedImages?.length > 0}
          />
          <button
            style={{ marginTop: "1rem" }}
            className="a"
            onClick={() => {
              setMapPopup(null);
              lookupReListing(item.id);
            }}
          >
            More Info..
          </button>
        </div>
      </MapBoxMapPopup>
    );
  };

  const locatorFeatureClicked = (mapWithEvt, properties) => {
    let item = locatorGeoJson.features.find(
      feature => feature.properties.id === properties.id
    );
    if (item) {
      setMapPopup(mapPopupElement(mapWithEvt, { ...item.properties }));
    }
  };

  const onMapClicked = (lat, lon) => {
    // console.log("onMapClicked", lat, lon);
    setMapPopup(null);
    setSelectedEventId(null);
  };

  const [selectedEventId, setSelectedEventId] = useState(null);

  const onAssetGroupPointClicked = (evt, pinLayerId, point) => {
    const pinLayer = pinLayers?.find(pl => pl.id === pinLayerId);
    const pinLayerPoint = pinLayer?.points?.find(p => p.id === point.id);
    if (pinLayerPoint) {
      setSelectedEventId(null);
      setMapPopup(<PinLayerPointPopup pinLayerPoint={pinLayerPoint} />);
    }
  };

  const onEventClicked = useCallback(
    (entry, event) => {
      setSelectedEventId(event.id);
      setMapPopup(
        <LocatorEventPopup
          resourceName={profile.resourceName}
          entry={entry}
          event={event}
        />
      );
    },
    [profile]
  );

  const { defaultCenter: mapDefaultCenter, boundingBox: boundaryBoundingBox } =
    useBoundaryDefaultCenter(systemLayers, defaultCenter);

  const eventMarkers = useLocatorEventMarkers(
    eventEntries,
    selectedEventId,
    onEventClicked
  );

  const onSearchTextChanged = searchText => {
    setSelectedCategory(null);
    setTextQuery(null);
    setSearchResults(null);
    setFilteredResults(null);
    setItemHovered(null);
    setItemSelected(null);
    setSearchText(searchText);
    // setCurrentFilter(null);
    if (!searchText || searchText.length === 0) {
      onREFilterChange(currentFilter);
    }
  };

  useEffect(() => {
    if (selectedCategory) {
      let mapToField = null;
      let operand = null;

      if (selectedCategory) {
        mapToField = "ADDRESS_UNIT_TYPE";
        operand = "EQUAL";
      }

      if (mapToField) {
        api
          .cms_reListingQuery(
            profile.resourceName,
            {
              queryObject: "RE",
              criteria: [
                {
                  mapToField: mapToField,
                  operand: operand,
                  value: selectedCategory.value
                }
              ]
            },
            token
          )
          .then(results => {
            let expandedResults = buildExpandedResults(results);
            // You can now use expandedResults instead of results, if needed.

            setSearchResults(expandedResults);

            setLocatorFilter(expandedResults.map(result => result.id));
            setFilteredResults(expandedResults);
          })
          .catch(error => {
            console.error("PUBLIC QUERY error", error);
          });
      }
    } else if (textQuery) {
      api
        .cms_reListingQuery(
          profile.resourceName,
          {
            queryObject: "RE",
            searchText: textQuery
          },
          token
        )
        .then(results => {
          let expandedResults = buildExpandedResults(results);
          setSearchResults(expandedResults);
          setFilteredResults(expandedResults);
          setLocatorFilter(expandedResults.map(result => result.id));
        });
    }
  }, [selectedCategory, textQuery]);

  const buildExpandedResults = results => {
    let expandedResults = [];
    for (const result of results) {
      expandedResults.push({
        ...result,
        location: {
          longitude: result.longitude,
          latitude: result.latitude,
          streetAddress: result.addressLine1
        },
        id: result.id
      });
    }
    return expandedResults;
  };

  const lookupReListing = id => {
    api.cms_getREListing(profile.resourceName, id, token).then(listing => {
      setItemSelected(listing);
    });
  };

  useEffect(() => {
    if (searchOptions && rESearchIndex && !allSearchOptions) {
      setAllSearchOptions(searchOptions.concat(rESearchIndex));
    }
  }, [searchOptions, rESearchIndex, allSearchOptions]);

  const searchResultsOpen =
    searchResults != null || (itemSelected && !selectedCategory);
  const previewOpen =
    searchText.length > 0 && !itemSelected && searchResults == null;
  if (allSearchOptions) {
    return (
      <>
        <div
          className={`locator-container-header${
            searchResultsOpen ? " search-results-open" : ""
          }`}
        >
          <LocatorSearchComponent
            searchMode={"re"}
            defaultText={selectedCategory?.label ?? searchText}
            onChange={onSearchTextChanged}
            searchResultsOpen={searchResultsOpen}
            previewOpen={previewOpen}
            onEnterPressed={val => {
              setSearchText(val);
              setTextQuery(val);
            }}
          />
          <div
            className={`search-filters-container${
              searchResultsOpen ? " search-results-open" : ""
            }`}
          >
            {allowFullscreen && <LocatorFullscreenControl />}
            <RELocatorFilters
              onFilterChange={onREFilterChange}
              searchResults={searchResults}
            />
          </div>
          {brandBadge && <LocatorBrandBadge {...brandBadge} />}
        </div>
        <LocatorSearchMenu
          showTextSearch={true}
          searchOptions={allSearchOptions}
          show={
            searchText.length > 0 &&
            selectedCategory == null &&
            textQuery == null &&
            !itemSelected
          }
          searchText={searchText}
          onSelect={selected => {
            if (selected.searchType === "RE") {
              lookupReListing(selected.value);
            } else if (selected?.searchType === "TEXT") {
              setSearchText(selected.value);
              setTextQuery(selected.value);
            } else {
              setSelectedCategory(selected);
            }
          }}
          searchMode={"re"}
        />
        <div
          className={`locator-container-results ${
            searchResults != null || (itemSelected && !selectedCategory)
              ? "show"
              : ""
          }`}
        >
          <LocatorSearchResultsComponent
            community={community}
            searchResults={filteredResults ?? searchResults}
            onItemHovered={result => setItemHovered(result)}
            onItemSelected={result => setItemSelected(result)}
            searchMode={"re"}
            textQuery={textQuery}
            selectedCategory={selectedCategory}
            currentLocation={currentLocation}
            currentFilter={currentFilter}
          />
        </div>
        <REListingModal
          name={profile.resourceName}
          reListing={itemSelected}
          onHide={() => setItemSelected(null)}
        />
        <MapBoxMapWrapper
          width="100vw"
          height="100vh"
          addMyLocation={true}
          type="MAP"
          defaultCenter={mapDefaultCenter}
          perspective={perspective}
          defaultZoom={15}
          defaultFlyTo={false}
          onMapClicked={onMapClicked}
          assetGroups={pinLayers?.length ? pinLayers : null}
          selectedPinLayers={null}
          onAssetGroupPointClicked={
            pinLayers?.length ? onAssetGroupPointClicked : null
          }
          markers={eventMarkers}
          systemLayers={systemLayers}
          mapCenterPadding={20}
          geoJson={backgroundGeoJson}
          onGeoJsonFeatureClicked={null}
          buildingsGeoJson={buildingsGeoJson}
          locatorGeoJson={filteredLocatorGeoJson}
          onLocatorGeoJsonFeatureClicked={locatorFeatureClicked}
          hoveredLocatorGeoJsonFeature={itemHovered?.id}
          selectedLocatorGeoJsonFeature={itemSelected?.id}
          boundingBox={boundaryBoundingBox ?? boundingBox}
          mapToggle={{
            assetGroups: true,
            geoJson: true,
            locator: true,
            buildings: true
          }}
          popupObject={mapPopup}
          horizontalOffset={isSm ? 0 : itemSelected ? screen.width * 0.35 : 0}
        />
      </>
    );
  } else {
    return <></>;
  }
};

export default RELocatorContainer;
